// frontend/src/pages/public/About/AboutPage.tsx
import React from "react";
import { Link } from "react-router-dom";
import SEO from "../../../components/SEO/SEO";
import PublicHeader from "../../../components/PublicHeader/PublicHeader";
import PublicFooter from "../../../components/PublicFooter/PublicFooter";

import {
  aboutHero,
  aboutWhy,
  aboutWhat,
  aboutWhatNot,
  aboutClinicAutonomy,
  aboutPrivacy,
  aboutCtas,
} from "./AboutContent";

import "./AboutPage.css";

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const SearchIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.3-4.3"></path>
  </svg>
);

const SendIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m22 2-7 20-4-9-9-4Z"></path>
    <path d="M22 2 11 13"></path>
  </svg>
);

const BellIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
  </svg>
);

const ShieldIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  </svg>
);

const UsersIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const BuildingIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
    <path d="M9 22v-4h6v4"></path>
    <path d="M8 6h.01"></path>
    <path d="M16 6h.01"></path>
    <path d="M12 6h.01"></path>
    <path d="M12 10h.01"></path>
    <path d="M12 14h.01"></path>
    <path d="M16 10h.01"></path>
    <path d="M16 14h.01"></path>
    <path d="M8 10h.01"></path>
    <path d="M8 14h.01"></path>
  </svg>
);

export default function AboutPage() {
  return (
    <div className="ocf-about">
      <SEO
        title="About"
        description="AcceptingMD is a standardized primary care intake and attachment workflow built by family doctors to support Ontario patients seeking a family doctor."
      />
      <PublicHeader variant="solid" />

      {/* Hero */}
      <header className="ocf-about-hero">
        <div className="ocf-about-hero-inner">
          <div className="ocf-about-hero-badge">{aboutHero.eyebrow}</div>
          <h1>{aboutHero.title}</h1>
          <p className="ocf-about-hero-text">{aboutHero.subtitle}</p>
          <div className="ocf-about-hero-tagline">
            <span className="ocf-about-hero-tagline-text">Not a directory. Not a marketplace.</span>
            <span className="ocf-about-hero-tagline-highlight">Intake infrastructure.</span>
          </div>
        </div>
      </header>

      {/* How It Works - Simple 3 column */}
      <section className="ocf-about-how">
        <div className="ocf-about-how-inner">
          <div className="ocf-about-how-item">
            <div className="ocf-about-how-icon"><SearchIcon /></div>
            <h3>Search</h3>
            <p>Find clinics accepting patients</p>
          </div>
          <div className="ocf-about-how-item">
            <div className="ocf-about-how-icon"><SendIcon /></div>
            <h3>Request</h3>
            <p>Submit your information</p>
          </div>
          <div className="ocf-about-how-item">
            <div className="ocf-about-how-icon"><BellIcon /></div>
            <h3>Hear Back</h3>
            <p>Get notified of updates</p>
          </div>
        </div>
      </section>

      <main className="ocf-about-main">
        {/* Why Section - Simple intro */}
        <section className="ocf-about-why">
          <h2>{aboutWhy.title}</h2>
          <p>{aboutWhy.description}</p>
          <div className="ocf-about-why-highlight">
            {aboutWhy.conclusion}
          </div>
        </section>

        {/* What It Does vs What It Doesn't - Side by Side */}
        <section className="ocf-about-comparison">
          <div className="ocf-about-comparison-card ocf-about-comparison-card--does">
            <h3>{aboutWhat.title}</h3>
            <p>{aboutWhat.description}</p>
            <ul className="ocf-about-comparison-list">
              {aboutWhat.features.map((feature, idx) => (
                <li key={idx}>
                  <span className="ocf-about-icon-check"><CheckIcon /></span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <div className="ocf-about-comparison-note">
              {aboutWhat.note}
            </div>
          </div>

          <div className="ocf-about-comparison-card ocf-about-comparison-card--doesnt">
            <h3>{aboutWhatNot.title}</h3>
            <p>{aboutWhatNot.description}</p>
            <ul className="ocf-about-comparison-list">
              {aboutWhatNot.items.map((item, idx) => (
                <li key={idx}>
                  <span className="ocf-about-icon-x"><XIcon /></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="ocf-about-comparison-disclaimer">
              {aboutWhatNot.disclaimer}
            </div>
          </div>
        </section>

        {/* Principles Grid */}
        <section className="ocf-about-principles">
          <h2 className="ocf-about-principles-title">Our Principles</h2>

          <div className="ocf-about-principles-grid">
            {/* Clinic Control */}
            <div className="ocf-about-principle-card ocf-about-principle-card--blue">
              <div className="ocf-about-principle-icon">
                <BuildingIcon />
              </div>
              <h3>Clinic Control</h3>
              <p>{aboutClinicAutonomy.clinicControl.description}</p>
              <ul>
                {aboutClinicAutonomy.clinicControl.items.map((item, idx) => (
                  <li key={idx}>
                    <CheckIcon />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Patient Clarity */}
            <div className="ocf-about-principle-card ocf-about-principle-card--green">
              <div className="ocf-about-principle-icon">
                <UsersIcon />
              </div>
              <h3>Patient Clarity</h3>
              <p>{aboutClinicAutonomy.patientClarity}</p>
              <div className="ocf-about-principle-note">
                {aboutClinicAutonomy.note}
              </div>
            </div>

            {/* Privacy */}
            <div className="ocf-about-principle-card ocf-about-principle-card--purple">
              <div className="ocf-about-principle-icon">
                <ShieldIcon />
              </div>
              <h3>{aboutPrivacy.title}</h3>
              <p>{aboutPrivacy.description}</p>
              <ul>
                {aboutPrivacy.items.map((item, idx) => (
                  <li key={idx}>
                    <CheckIcon />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </main>

      {/* CTA with Built By */}
      <section className="ocf-about-cta">
        <div className="ocf-about-cta-inner">
          <div className="ocf-about-cta-badge">{aboutCtas.badge}</div>
          <h2>{aboutCtas.title}</h2>
          <p>{aboutCtas.subtitle}</p>
          <div className="ocf-about-cta-buttons">
            <Link to={aboutCtas.primaryCta.to} className="ocf-about-cta-btn ocf-about-cta-btn--primary">
              {aboutCtas.primaryCta.label}
            </Link>
            <Link to={aboutCtas.secondaryCta.to} className="ocf-about-cta-btn ocf-about-cta-btn--ghost">
              {aboutCtas.secondaryCta.label}
            </Link>
            <Link to={aboutCtas.contactCta.to} className="ocf-about-cta-btn ocf-about-cta-btn--ghost">
              {aboutCtas.contactCta.label}
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
